/**
 * The party record's `Licences` panel — and the two things it refuses to pretend.
 *
 * ===========================================================================
 * ⚠ ABSENT, NOT EMPTY, FOR A READER WITHOUT `quotes.view`
 * ===========================================================================
 * This panel lives inside a page gated on `parties.view`, and the route it calls
 * is `@RequirePerm('quotes.view')` — a genuinely different key, held by different
 * seats. So a reader with one and not the other gets the party WITHOUT the tab,
 * and the panel returns `null` rather than a card headed "Licences" containing
 * nothing. The difference is the whole point: an empty card is a statement about
 * the PARTY ("this customer has no licences"), and a missing card is a statement
 * about the ACCOUNT. Only the second one is true. It is the same
 * field-absent-rather-than-null rule the payloads keep, applied to a panel.
 *
 * Nothing is fetched either, which is the half that a "renders nothing" check
 * would miss: a request that 403s still went out.
 *
 * ===========================================================================
 * ⚠ IT SEARCHES BY NAME, AND IT SAYS SO ON EVERY ROW
 * ===========================================================================
 * The licence register's customers are its OWN canonical names, not CRM parties.
 * `tally_licence_customers.party_id` is NULL for every one of the 439 of them and
 * stays NULL until owner decision D-2 — the workbook records no country for most
 * of these names, and `resolveCustomerCountry` refuses to invent one, so linking
 * is a ruling rather than a join.
 *
 * That leaves this panel two honest choices: show nothing until D-2, or search
 * the register by name and label the result as what it is. It does the second.
 * Every row says `Name match only`, because a name match is a lead and not an
 * identity — and the day a customer really does carry this party's id, that row
 * says `Linked to this party` instead, from the payload rather than from a
 * release note. Nothing here writes a link; only the owner's ruling does.
 *
 * No activation key and no money appear, for the reasons `api.ts` records: the
 * register holds neither.
 */

import { useQuery } from '@tanstack/react-query';
import { Link, useParams, useSearchParams } from 'react-router-dom';

import { useCountryCode } from '../../auth/guards';
import { useSession } from '../../auth/session';
import type { CountryCode } from '../../domain/countries';
import { Alert, Card, Chip, SkeletonRows } from '../../components/ui';
import { cn } from '../../lib/cn';
import { fetchLicenceCustomers, licenceErrorText, licenceKeys } from './api';
import type { LicenceClassification } from './api';

const MATCH_LIMIT = 10;

const CLASSIFICATION_LABEL: Readonly<Record<string, string>> = {
  customer: 'Customer',
  reseller: 'Reseller',
  group_entity: 'Group entity',
  undecided: 'Undecided',
};

function classificationChip(value: LicenceClassification | null): React.JSX.Element | null {
  if (value === null) return null;
  return (
    <Chip
      className={cn(
        value === 'group_entity' && 'bg-info-bg text-info-fg',
        value === 'undecided' && 'bg-warn-bg text-warn-fg',
      )}
    >
      {CLASSIFICATION_LABEL[value] ?? value}
    </Chip>
  );
}

export function PartyLicenceTab({
  countryCode,
  partyId,
  partyName,
}: {
  /** The active country — `can()` is evaluated PER COUNTRY, so this is not a string. */
  countryCode: CountryCode;
  /** The CRM party this panel is sitting on. Compared to each match's `party_id`. */
  partyId: string;
  /** The host page already holds the name; re-fetching it would be a second source. */
  partyName: string;
}): React.JSX.Element | null {
  const { can } = useSession();
  const canView = can(countryCode, 'quotes.view');
  const term = partyName.trim();
  const filters = { q: term, perPage: MATCH_LIMIT };

  const matches = useQuery({
    queryKey: licenceKeys.customers(countryCode, filters),
    queryFn: ({ signal }) => fetchLicenceCustomers(countryCode, filters, signal),
    enabled: canView && term !== '',
  });

  // See the header: absent, not empty. And before the `enabled` flag, so no request
  // is prepared for a reader who may not make it.
  if (!canView) return null;

  const rows = matches.data?.data ?? [];

  return (
    <Card title="Licences" className="w-full">
      <div data-testid="party-licence-tab" className="flex flex-col gap-3">
        {term === '' ? (
          <Alert tone="info" title="This panel searches the register by the party’s name">
            No name was supplied with this party, so there is nothing to search for. Open the
            panel from the party record, where the name travels with it.
          </Alert>
        ) : matches.isPending ? (
          <SkeletonRows rows={3} label="Looking for licences under this name" />
        ) : matches.isError ? (
          <Alert tone="danger" title="The licence register could not be searched">
            <p>{licenceErrorText(matches.error)}</p>
          </Alert>
        ) : rows.length === 0 ? (
          <p className="text-label text-fg-secondary">
            No licence-register customer matches this party’s name. That is a search result,
            not a statement that this customer holds no licences — the register is keyed on
            the workbook’s own names.
          </p>
        ) : (
          <ul className="flex flex-col">
            {rows.map((row) => {
              const linked = row.party_id !== null && row.party_id === partyId;
              return (
                <li
                  key={row.id}
                  className="flex flex-wrap items-center gap-x-3 gap-y-1.5 border-b border-line py-2.5 last:border-b-0"
                >
                  <Link
                    to={`/countries/${countryCode}/licences?customer=${row.id}`}
                    className="text-body font-medium text-indigo underline-offset-2 hover:underline"
                  >
                    {row.canonical_name}
                  </Link>
                  {classificationChip(row.classification)}
                  {/*
                   * The owner's partner flag (migration 36): renewals for a partner's
                   * licences are chased THROUGH the partner, so the reader deciding who
                   * to call needs the word on the row. QUIET — the default chip, no
                   * tone — and rendered only when the flag is SET: flagging the absence
                   * on every ordinary customer would drown the handful of partners the
                   * flag exists to surface. Read-only, because no HTTP write for it
                   * exists (the importer's apply step is the only writer).
                   */}
                  {row.is_partner ? <Chip>Partner</Chip> : null}
                  <Chip className={cn(linked ? 'bg-ok-bg text-ok-fg' : 'bg-warn-bg text-warn-fg')}>
                    {linked ? 'Linked to this party' : 'Name match only'}
                  </Chip>
                  <span className="tnum text-label text-fg-secondary">
                    {String(row.licence_count)}{' '}
                    {row.licence_count === 1 ? 'licence' : 'licences'} ·{' '}
                    {String(row.event_count)} {row.event_count === 1 ? 'event' : 'events'}
                  </span>
                  {row.last_event_on === null ? null : (
                    <span className="tnum text-label text-fg-muted">
                      last {row.last_event_on}
                    </span>
                  )}
                </li>
              );
            })}
          </ul>
        )}

        {/*
         * THE D-2 SENTENCE IS UNCONDITIONAL while any row is a name match, because
         * that is the state of the data rather than a state of this screen.
         */}
        <p className="text-label text-fg-muted">
          The licence register keeps its own canonical customer names. None of them is linked
          to a CRM party yet — that link is owner decision D-2 — so a row above is a name
          match unless it says otherwise.
        </p>

        {(matches.data?.disclosures ?? []).map((line) => (
          <p key={line} className="text-label text-fg-muted">
            {line}
          </p>
        ))}
      </div>
    </Card>
  );
}

/* ═════════════════════════════════════════════════════════════════════════
 * THE ROUTE — BUILT BEFORE THE PAGE THE PANEL BELONGS TO EXISTED, KEPT AFTER
 * ═════════════════════════════════════════════════════════════════════════
 * When this shipped, the party record page did not exist and building it was
 * outside this cluster's scope, so the panel was a PROPS-DRIVEN COMPONENT "the
 * party record can import in one line" plus this thin wrapper at
 * `parties/:partyId/licences`. That page has since landed
 * (feat/party-referral-screen, `PartyRecordPage`) and imports the panel at
 * exactly that one-line cost — nothing had to move, which was the point.
 *
 * This wrapper STAYS for deep links. The name arrives as `?name=`, because a
 * host page already holds the party and re-fetching it here would make two
 * sources for one string; without it the panel says so rather than searching
 * for nothing.
 * ====================================================================== */
export function PartyLicencesRoute(): React.JSX.Element {
  const countryCode = useCountryCode();
  const { partyId = '' } = useParams<{ partyId: string }>();
  const [params] = useSearchParams();

  return (
    <div className="flex flex-col gap-4 p-6">
      <PartyLicenceTab
        countryCode={countryCode}
        partyId={partyId}
        partyName={params.get('name') ?? ''}
      />
    </div>
  );
}
